import { useCachedUserInfo, useCachedUserType, useTranslation } from 'hooks';
import { FC, useState } from 'react';
import cls from './websites.module.scss';
import { ICONS } from 'shared/constants/images';
import { InputField } from 'shared/ui/inputField/inputField';
import { DomainsTable } from './domains-table/domains-table';
import Button from 'shared/ui/button/button';
import { AddWebsiteModal } from '../domain-modal/add-website-modal';
import { SubscriptionCard } from '../subscription-card/subscription-card';
import { Icon } from 'shared/ui/icon/icon';
import { Filter } from 'shared/constants/color-filters';
import { Link } from 'react-router';
import { track } from 'utils/mixpanel';
import { ComparePlansDrawer } from '../compare-plans-drawer/compare-plans-drawer';
import { useSubscriptions } from 'hooks/api/useSubscriptions';
import { StoreModal } from '../store-modal/store-modal';
import { BuySubscriptionModal } from '../buy-subscription-modal/buy-subscription-modal';
import { Card } from 'shared/ui/card/card';
import { WhatsIncludedDrawer } from 'pages/subscriptions/ui/whats-included-drawer/whats-included-drawer';

interface WebsitesProps {
  subscriptionQuantity: number;
  onSubscriptionQuantityChange: (quantity: number) => void;
}

export const Websites: FC<WebsitesProps> = ({
  subscriptionQuantity,
  onSubscriptionQuantityChange,
}) => {
  const { t } = useTranslation();
  const { totalCount, unassignedCount } = useCachedUserInfo();
  const { storeUrl } = useSubscriptions();
  const [isAddWebsiteModalOpen, setIsAddWebsiteModalOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isStoreModalOpen, setIsStoreModalOpen] = useState(false);
  const [isBuySubscriptionModalOpen, setIsBuySubscriptionModalOpen] =
    useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { isKa, isDirect } = useCachedUserType();

  const handleBuySubscription = () => {
    track('AIS:LICENSE_MGMT-BUY_SUBSCRIPTION-CLICKED');
  };

  const handleComparePlansOpen = () => {
    track('AIS:LICENSE_MGMT-COMPARE_PLANS-OPENED');
    setIsDrawerOpen(true);
  };

  const handleSubWithoutUrl = () => {
    track('AIS:LICENSE_MGMT-BUY_SUBSCRIPTION-CLICKED-NO_URL');
    setIsStoreModalOpen(true);
  };

  const handleBuySubscriptionStdln = () => {
    track('AIS:LICENSE_MGMT-BUY_SUBSCRIPTION-CLICKED');
    setIsBuySubscriptionModalOpen(true);
  };

  const handleBuyLicenses = () => {
    if (isDirect) {
      setIsBuySubscriptionModalOpen(true);
    } else if (storeUrl) {
      window.open(storeUrl, '_blank', 'noopener,noreferrer');
    } else {
      setIsStoreModalOpen(true);
    }
  };

  return (
    <>
      <div className={cls.subscriptionCards}>
        <div
          data-testid='websites-top-info-card'
          className={`${cls.infoCard} ${isDirect ? cls.stdln : ''}`}
        >
          <div>
            <h3>Riva {t('license')}</h3>
            <p>
              {isDirect
                ? t('subscriptionsDescStdln')
                : t('subscriptionDescription')}
            </p>
          </div>
          <div className={cls.buttonGroup}>
            {isDirect && (
              <Button
                theme='filled'
                tone='default'
                onClick={handleBuySubscriptionStdln}
              >
                <span>{t('buySubscriptions')}</span>
              </Button>
            )}
            <Button
              theme={isDirect ? 'text' : 'tonal'}
              tone='default'
              onClick={handleComparePlansOpen}
            >
              {t('whatsIncluded')}
            </Button>
          </div>
        </div>
        {isKa && (
          <div className={`${cls.infoCard} ${cls.borders}`}>
            <div>
              <h3>{t('buySubscriptions')}</h3>
              <p>{t('buySubscriptionsDescription')}</p>
            </div>
            {storeUrl ? (
              <Link
                className={cls.linkBtn}
                to={storeUrl}
                target='_blank'
                rel='noopener noreferrer'
                onClick={handleBuySubscription}
              >
                <Icon pathToIcon={ICONS.newTab} filter={Filter.white} />
                <span>{t('buySubscriptions')}</span>
              </Link>
            ) : (
              <Button
                theme='filled'
                tone='default'
                onClick={handleSubWithoutUrl}
              >
                <Icon pathToIcon={ICONS.newTab} filter={Filter.white} />
                <span>{t('buySubscriptions')}</span>
              </Button>
            )}
          </div>
        )}
        <SubscriptionCard />
      </div>
      <Card className={cls.websitesCard}>
        <div className={cls.websitesTitle}>
          <h2>{t('websites')}</h2>
          <p>
            {totalCount} {t('total')} • {unassignedCount} {t('notAssigned')}
          </p>
        </div>
        <div className={cls.websitesHeader}>
          <InputField
            placeholder={t('search') + '...'}
            icon={ICONS.search}
            className={cls.searchInput}
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          <Button
            theme='filled'
            tone='default'
            onClick={() => setIsAddWebsiteModalOpen(true)}
          >
            {t('add_website')}
          </Button>
        </div>
        <DomainsTable
          searchQuery={searchQuery}
          onBuyLicenses={handleBuyLicenses}
        />
      </Card>
      {isAddWebsiteModalOpen && (
        <AddWebsiteModal
          isOpen={isAddWebsiteModalOpen}
          onClose={() => setIsAddWebsiteModalOpen(false)}
        />
      )}
      {isStoreModalOpen && (
        <StoreModal
          isOpen={isStoreModalOpen}
          onClose={() => setIsStoreModalOpen(false)}
        />
      )}
      {isBuySubscriptionModalOpen && (
        <BuySubscriptionModal
          isOpen={isBuySubscriptionModalOpen}
          onClose={() => setIsBuySubscriptionModalOpen(false)}
          quantity={subscriptionQuantity}
          onQuantityChange={onSubscriptionQuantityChange}
        />
      )}
      {isDirect ? (
        <WhatsIncludedDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
        />
      ) : (
        <ComparePlansDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
        />
      )}
    </>
  );
};
